import {connect} from 'react-redux';

import {AsyncStatusesEnum} from '../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {getShouldHideAssignedConversations} from '../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {AppContentTypesEnum} from '../../../libs/shared/core/src/store/panels/app/commonAppPanelState';
import {
  InboxesPanelId,
  makeCellsPanelId,
  makeViewerPanelId,
} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../srcCommon/helpers/reselectHelpers';
import {makeGetHasCellsFilters} from '../../../srcCommon/inboxes/cells/cellsFiltersSelectors';
import {updateSizeAction} from '../../store/data/localPreferences/localPreferencesActions';
import {getSizes} from '../../store/data/localPreferences/localPreferencesSelectors';
import {clearOfflineDrafts, reopenOfflineDraftMessages} from '../../store/data/offline/offlineActions';
import {getUserStarredTagId} from '../../store/data/tags/tagsSelectors';
import {getUser} from '../../store/data/teammates/teammatesSelectors';
import {getShouldShowEditModeHelpTip} from '../../store/data/workspace/visualWorkspace/visualWorkspaceSelectors';
import {pushParametersAction, replaceParametersAction} from '../../store/panels/app/appPanelRouteActions';
import {
  CellsPanelPageSelector,
  makeGetCellsPanelCurrentPage,
} from '../../store/panels/cells/cellsPanelSelectors';
import {
  makeGetInboxesIsEditingWorkspaces,
  makeGetInboxesParameters,
} from '../../store/panels/inboxes/inboxesPanelSelectors';
import {
  InboxesConversationsParameters,
  InboxesTeammateMessagesParameters,
} from '../../store/panels/inboxes/parameters/inboxesParameters';
import {makeGetContentPage, makeGetViewerPanel} from '../../store/panels/viewer/viewerPanelSelectors';
import {getIsSelectedSidePanelFrameNative} from '../../store/stateless/sidePanelFrameSelectors';
import {Inboxes} from './inboxes';

/*
 * Props.
 */

interface InboxesContainerProps {
  panelId: InboxesPanelId;
}

/*
 * Selectors.
 */

function getCellsPanelId(_state: unknown, props: InboxesContainerProps) {
  return makeCellsPanelId(props.panelId);
}

function getViewerPanelId(_state: unknown, props: InboxesContainerProps) {
  return makeViewerPanelId(props.panelId);
}

function makeGetInboxesStatus(getCellsPage: CellsPanelPageSelector<InboxesContainerProps>) {
  const getViewerPage = makeGetContentPage(makeGetViewerPanel(getViewerPanelId));

  return createSelector([getCellsPage, getViewerPage], (cellsPage, viewerPage): AsyncStatusesEnum => {
    // If P3 is not loaded, show the P2 loading/error state across P2/P3.
    if (viewerPage.status !== AsyncStatusesEnum.LOADED) {
      return cellsPage.status;
    }

    // Show P2 and P3 individually.
    return AsyncStatusesEnum.LOADED;
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<InboxesContainerProps>()(() => {
  const getCellsPanelCurrentPage = makeGetCellsPanelCurrentPage(getCellsPanelId);

  return {
    status: makeGetInboxesStatus(getCellsPanelCurrentPage),
    user: getUser,
    sizes: getSizes,
    parameters: makeGetInboxesParameters(),
    isEditingWorkspaces: makeGetInboxesIsEditingWorkspaces(),
    // Use the current cells panel page to look up the filters, because that matches the CellsHeaderContainer behavior.
    hasCellsFilters: makeGetHasCellsFilters(getCellsPanelCurrentPage),
    shouldShowEditModeHelpTip: getShouldShowEditModeHelpTip,
    starredTagId: getUserStarredTagId,
    shouldHideAssignedConversations: getShouldHideAssignedConversations,
    isSelectedSidePanelFrameNative: getIsSelectedSidePanelFrameNative,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<InboxesContainerProps>()(({panelId}) => ({
  onNavigate: (parameters: InboxesConversationsParameters | InboxesTeammateMessagesParameters) =>
    pushParametersAction(panelId, {
      contentType: AppContentTypesEnum.INBOXES,
      parameters,
    }),
  onParametersChange: (parameters: InboxesConversationsParameters | InboxesTeammateMessagesParameters) =>
    replaceParametersAction(panelId, {
      contentType: AppContentTypesEnum.INBOXES,
      parameters,
    }),
  updateSize: updateSizeAction,
  reopenOfflineDraftMessages,
  clearOfflineDrafts,
}));

export const InboxesContainer = connect(mapStateToProps, mapDispatchToProps)(Inboxes);
